/* ============================================================================
   ICONOS DE HERRAMIENTA

   Uno por pestaña, con la MISMA clave que usa `TABS` en el legacy, para que la
   navegación no tenga que traducir nombres.

   Cada icono dibuja el instrumento o el artefacto real de esa herramienta: la
   envolvente de CG es una envolvente con el punto adentro, el FRAT es una
   matriz 5×5 con la celda evaluada, el SGS es un bow-tie, el espacio aéreo es
   el escalonado de sectores. No hay engranajes ni carpetas: un piloto reconoce
   antes el dibujo de su propia herramienta que una metáfora de oficina.

   Rejilla 24×24, trazo 1,6, sin relleno, color heredado. Sin relleno porque el
   color lo pone quien lo usa: el mismo icono sirve apagado, activo, o en rojo
   dentro de un aviso.
   ========================================================================== */

import React from "react";

const base = (tam) => ({
  width: tam,
  height: tam,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
});

/** Helicóptero en planta, tomado del ícono de marca. Aeronave activa. */
export const Aeronave = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <ellipse cx="12" cy="11" rx="2.1" ry="4.2" />
    <path d="M12 3.4v1.6M12 15.2v4.1" />
    <path d="M4.2 7.5 19.8 14.5M19.8 7.5 4.2 14.5" strokeWidth="1.1" />
    <path d="M9.6 18.1h4.8" />
    <path d="M10.4 19.3v1.6M13.6 19.3v1.6" strokeWidth="1.1" />
    <path d="M12 20.6h1.9" strokeWidth="1.1" />
  </svg>
);

/** Planificación de ruta: aguja de rumbo sobre un tramo con waypoints. */
export const Ruta = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M12 2.6 15.4 11 12 9.2 8.6 11z" />
    <path d="M12 12.4v3.1" strokeWidth="1.1" />
    <path d="M4 20.4h3.6l4.4-4.4 4.4 4.4H20" />
    <circle cx="7.6" cy="20.4" r="1.1" />
    <circle cx="16.4" cy="20.4" r="1.1" />
  </svg>
);

/** Análisis de vuelo: la envolvente de CG con el punto calculado adentro. */
export const Analisis = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M3.4 21V4M3.4 21h17.2" strokeWidth="1.1" />
    <path d="M7.2 18.4V9.6l4.2-2.8 6.4 1.9v4.6l-3.1 5.1z" />
    <circle cx="12.1" cy="13" r="1.5" />
    <path d="M12.1 10.6v-1M12.1 16.4v-1M9.7 13h-1M15.5 13h-1" strokeWidth="1.1" />
  </svg>
);

/** Espacio aéreo: el escalonado de sectores visto de perfil. */
export const Espacio = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M2.6 20.4h18.8" strokeWidth="1.1" />
    <path d="M2.6 16.2h5.2v-4h4.4v-4h6.6" />
    <path d="M7.8 16.2v4.2M12.2 12.2v8.2M18.8 8.2v12.2" strokeWidth=".9" strokeDasharray="2 2" />
    <path d="M18.8 8.2h2.6" />
  </svg>
);

/** FRAT: la matriz de riesgo 5×5 con la celda evaluada marcada. */
export const Frat = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <rect x="3.6" y="3.6" width="16.8" height="16.8" rx=".6" />
    <path d="M6.96 3.6v16.8M10.32 3.6v16.8M13.68 3.6v16.8M17.04 3.6v16.8" strokeWidth=".9" />
    <path d="M3.6 6.96h16.8M3.6 10.32h16.8M3.6 13.68h16.8M3.6 17.04h16.8" strokeWidth=".9" />
    <rect x="13.68" y="6.96" width="3.36" height="3.36" fill="currentColor" stroke="none" opacity=".85" />
  </svg>
);

/** Resumen: la hoja de vuelo firmada. */
export const Resumen = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M5.4 3.6h9.2L18.6 7.6V20.4H5.4z" />
    <path d="M14.6 3.6v4h4" strokeWidth="1.1" />
    <path d="M8.4 11.4h7.2M8.4 14.2h7.2" strokeWidth="1.1" />
    <path d="M8.4 17.4c1.2-1.4 2-.4 3 .2s1.9.2 3.8-1" />
  </svg>
);

/** Operaciones: la bitácora, con renglones y hora. */
export const Operaciones = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M5 3.6h11.4L19 6.2V17" />
    <path d="M5 3.6V20.4h9.2" />
    <path d="M8 8h8M8 11.2h8M8 14.4h4.4" strokeWidth="1.1" />
    <circle cx="17.4" cy="18.4" r="3" />
    <path d="M17.4 16.9v1.6l1.1.8" strokeWidth="1.1" />
  </svg>
);

/** Mantenimiento: contador de horas con llave. */
export const Mantenimiento = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <circle cx="10.4" cy="10.4" r="6.4" />
    <path d="M7.6 10.4h5.6M10.4 7.6v2.8" strokeWidth="1.1" />
    <rect x="7.4" y="12.6" width="6" height="2.6" rx=".4" strokeWidth="1.1" />
    <path d="M15.4 15.6a2.9 2.9 0 0 0 4.1 4.1l-2-2 1.4-1.4z" />
  </svg>
);

/** SGS: el bow-tie — amenazas, evento central, consecuencias. */
export const Sms = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M9.4 12 3 6.8v10.4z" />
    <path d="M14.6 12 21 6.8v10.4z" />
    <circle cx="12" cy="12" r="2.4" />
    <path d="M3 9.4h3.4M3 14.6h3.4M17.6 9.4H21M17.6 14.6H21" strokeWidth="1.1" />
  </svg>
);

/** Importar aeronave: la planilla que entra al fuselaje. */
export const Importar = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <rect x="3" y="4.6" width="8.8" height="9.6" rx=".6" />
    <path d="M3 7.8h8.8M6.4 4.6v9.6" strokeWidth=".9" />
    <path d="M13.4 9.4h6.2M17.2 7l2.4 2.4-2.4 2.4" />
    <ellipse cx="16.6" cy="17.6" rx="1.5" ry="3" />
    <path d="M11.4 15.6h10.4" strokeWidth="1.1" />
  </svg>
);

/** Dashboard: tres instrumentos agrupados. */
export const Dashboard = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <circle cx="7.4" cy="8.6" r="4" />
    <path d="M7.4 8.6 9.6 6.6" strokeWidth="1.1" />
    <circle cx="16.6" cy="8.6" r="4" />
    <path d="M16.6 8.6 14.6 6.9" strokeWidth="1.1" />
    <path d="M4 20.4h5.2M4 16.8h9.4M12.6 20.4H20" strokeWidth="1.6" />
  </svg>
);

/** Aviso maestro: el triángulo del panel de anunciadores. */
export const Aviso = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M12 3.8 21.2 20H2.8z" />
    <path d="M12 10v4.4" />
    <circle cx="12" cy="17.2" r=".9" fill="currentColor" stroke="none" />
  </svg>
);

/** Combustible: el indicador de tanque con sus marcas. */
export const Combustible = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M3.6 17.4a8.4 8.4 0 0 1 16.8 0" />
    <path d="M4.6 12.6 5.9 13.4M12 8.8v1.5M19.4 12.6 18.1 13.4" strokeWidth="1.1" />
    <path d="M12 17.4 8.1 12.9" />
    <circle cx="12" cy="17.4" r="1.2" />
    <path d="M3.6 20.6h16.8" strokeWidth="1.1" />
  </svg>
);

/** Performance: la curva que cae con la altitud de densidad. */
export const Performance = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M3.4 21V4M3.4 21h17.2" strokeWidth="1.1" />
    <path d="M6 8c4.6.4 9.4 3.4 13.2 9.6" />
    <path d="M6 12.6c3.6.4 7.2 2.6 10 6.6" strokeWidth="1.1" strokeDasharray="2.4 2" />
    <circle cx="13.4" cy="13.6" r="1.3" />
  </svg>
);

/** Tripulación: el auricular de vuelo. */
export const Tripulacion = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M4.6 15.4v-3a7.4 7.4 0 0 1 14.8 0v3" />
    <rect x="2.8" y="13.4" width="3.6" height="6" rx="1.4" />
    <rect x="17.6" y="13.4" width="3.6" height="6" rx="1.4" />
    <path d="M17.6 19.4v.6a2 2 0 0 1-2 2h-2.2" strokeWidth="1.1" />
  </svg>
);

/** Base / helipuerto. */
export const Helipuerto = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <circle cx="12" cy="12" r="8.6" />
    <path d="M9.2 8.2v7.6M14.8 8.2v7.6M9.2 12h5.6" strokeWidth="1.8" />
  </svg>
);

/** Meteorología: la manga de viento. */
export const Viento = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M4.4 3v18" />
    <path d="M4.4 6.4 19.6 8.6v4.4L4.4 14.6z" />
    <path d="M9.4 7.1v6.9M14 7.8v5.5" strokeWidth="1.1" />
  </svg>
);

/** Empresa, usuarios y perfiles: quién depende de quién. El organigrama, que
    es lo que esa pantalla administra — ni una carpeta ni un engranaje. */
export const Empresa = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <circle cx="12" cy="5.4" r="2.3" />
    <circle cx="5.6" cy="18.2" r="2.3" />
    <circle cx="18.4" cy="18.2" r="2.3" />
    <path d="M12 7.7v3.4M12 11.1H5.6v4.8M12 11.1h6.4v4.8" strokeWidth="1.1" />
  </svg>
);

/** Modo vuelo: la aeronave sobre el mapa en movimiento — anillo de rumbo con
    la flecha de posición adentro. Distinto de Ruta, que es la aguja sobre el
    tramo a planificar. */
export const Vuelo = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M12 2.8v1.9M12 19.3v1.9M2.8 12h1.9M19.3 12h1.9" strokeWidth="1.1" />
    <path d="M12 6.8l2.5 8-2.5-1.8-2.5 1.8z" />
  </svg>
);

/** Consulta: la biblioteca de referencia — un libro abierto, que es lo que se
    mira y NO se llena. */
export const Consulta = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M12 6.6C10.2 5 7.8 4.3 4.4 4.6v13c3.4-.3 5.8.4 7.6 2 1.8-1.6 4.2-2.3 7.6-2v-13c-3.4-.3-5.8.4-7.6 2z" />
    <path d="M12 6.6v13" strokeWidth="1.1" />
  </svg>
);

/** Estadísticas: barras sobre un eje. Es lo que la pantalla hace —contar y
    comparar— sin dibujar un gráfico que todavía no existe. */
export const Estadisticas = ({ tam = 24 }) => (
  <svg {...base(tam)}>
    <path d="M3.6 20.4h16.8M3.6 20.4V3.6" strokeWidth="1.1" />
    <path d="M7.8 20.4v-5.2M12 20.4v-9.4M16.2 20.4v-3.4" />
  </svg>
);

/** Las claves son las de TABS en el legacy. */
export const ICONOS = {
  ruta: Ruta,
  analisis: Analisis,
  espacio: Espacio,
  frat: Frat,
  resumen: Resumen,
  operaciones: Operaciones,
  mantenimiento: Mantenimiento,
  sms: Sms,
  importar: Importar,
  dashboard: Dashboard,
  empresa: Empresa,
  estadisticas: Estadisticas,
  vuelo: Vuelo,
  consulta: Consulta,
  // Fuera de la navegación, para instrumentos y avisos.
  aeronave: Aeronave,
  aviso: Aviso,
  combustible: Combustible,
  performance: Performance,
  tripulacion: Tripulacion,
  helipuerto: Helipuerto,
  viento: Viento,
};

/**
 * Icono por nombre. Devuelve null si la clave no existe, en vez de romper el
 * render: una pestaña nueva sin icono debe seguir siendo usable.
 */
export default function Icono({ nombre, tam = 22 }) {
  const Componente = ICONOS[nombre];
  return Componente ? <Componente tam={tam} /> : null;
}
